import { DataSTag, Sel } from '#~/stag/index.js';
import { update as updateAgent } from '#~/agent/commands/index.js';
import { reboot } from '#~/device/commands/index.js';
import { raiseError } from '#~/notifications/index.js';
import { useMultiProxyStateQuery } from '#~/statequery/hook.js';
import { Button } from '@studio/design-system/button';
import { usePrompt } from '@studio/design-system/prompt';
import { Spinner } from '@studio/design-system/spinner';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import type { Agent } from '@studio/models/agent';
import { toVersion } from '@studio/models/agent';
import type { Device as M_Device } from '@studio/models/device';
import type { StatusUpdate } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { twMerge } from 'cn';
import { computeDownloadPercent, deriveUpdateStatus } from './firmware-status.helpers.js';

interface FirmwareStatusProps {
  agentId: Agent['id'];
  deviceId: M_Device['id'];
  className?: string;
}

export const FirmwareStatus = ({
  agentId,
  deviceId,
  className
}: FirmwareStatusProps): ReactElement => {
  const prompt = usePrompt();

  const proxyStateMap = useMultiProxyStateQuery<{
    encoderVersion: string | undefined;
    update: StatusUpdate | undefined;
  }>((state) => ({
    encoderVersion: state?.status?.encoder?.version,
    update: state?.status?.update
  }));

  const proxyState = proxyStateMap[agentId];
  const encoderVersion = proxyState?.encoderVersion;
  const stateUpdateStatus = proxyState?.update;

  // Reducer extracted to firmware-status.helpers.ts so the state-machine
  // logic is unit-testable per ai-code-requires-tests (global rule).
  const updateStatus = useMemo(() => deriveUpdateStatus(stateUpdateStatus), [stateUpdateStatus]);

  const handleDownloadUpdate = (): void => {
    prompt.confirm({
      title: 'Update Firmware?',
      message: `This will download the latest firmware for your ${vendorName()} device. You can install it after the download completes.`,
      okay: {
        label: 'Download Update'
      },
      onConfirm: (answer: boolean) => {
        if (answer) {
          updateAgent(agentId).catch((error) => raiseError(error, { actionable: true }));
        }
      }
    });
  };

  const handleInstallUpdate = (): void => {
    prompt.confirm({
      title: 'Install Firmware Update?',
      message: `This will reboot your ${vendorName()} device to apply the downloaded firmware update. The device will be temporarily unavailable during the update.`,
      okay: {
        label: 'Install & Reboot',
        variant: 'danger'
      },
      onConfirm: (answer: boolean) => {
        if (answer) {
          reboot(deviceId).catch((error) => raiseError(error, { actionable: true }));
        }
      }
    });
  };

  const downloadPercent = computeDownloadPercent(
    updateStatus.bytesDownloaded,
    updateStatus.bytesTotal
  );

  return (
    <div className={twMerge('flex flex-col gap-8', className)}>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-8'>
          <span className='text-md font-medium text-light'>Firmware</span>
          {encoderVersion && (
            <span
              {...DataSTag(Sel.VideoSettings, 'firmware-version', deviceId)}
              title={encoderVersion}
              className='text-md text-muted'
            >
              {toVersion(encoderVersion)}
            </span>
          )}
        </div>

        <div className='flex items-center gap-8'>
          {updateStatus.isInstalling && (
            <div className='flex items-center gap-6'>
              <Spinner className='h-16 w-16' />
              <span className='text-md text-muted'>Installing...</span>
            </div>
          )}

          {updateStatus.isDownloading && (
            <span className='text-md text-muted'>Downloading... {downloadPercent}%</span>
          )}

          {updateStatus.isReadyToInstall && (
            <Button size='sm' variant='danger' onClick={handleInstallUpdate}>
              Install Update
            </Button>
          )}

          {updateStatus.canStartDownload && (
            <Button size='sm' onClick={handleDownloadUpdate}>
              Update Firmware
            </Button>
          )}

          {!updateStatus.updateAvailable && !updateStatus.isInstalling && (
            <span className='text-md text-muted'>Up to date</span>
          )}
        </div>
      </div>

      {updateStatus.isDownloading && updateStatus.bytesTotal > 0 && (
        <div className='h-4 w-full overflow-hidden rounded-full bg-base-100'>
          <div
            className='transition-all duration-300 h-full rounded-full bg-primary'
            style={{ width: `${downloadPercent}%` }}
          />
        </div>
      )}
    </div>
  );
};
